export type ValLab = Record<string, unknown>

type TrackerFn = (...args: unknown[]) => unknown
type PageViewCase = ((...args: unknown[]) => void) | null

/** MV 停留阶段：开始 / 结束 */
export type MvStayType = 'start' | 'end'

/**
 * 统一 MV 上报参数：
 * - module_type: start | end（必填）
 * - durationMs: 仅 end 时传毫秒，内部转成「分钟」字段 duration
 * - 其余字段原样透传（如 module_identifier）
 */
export type ReportMvParams = {
  module_type: MvStayType
  /** 结束停留时的时长（毫秒），上报时转为分钟 */
  durationMs?: number
} & ValLab

function resolveEnv(): { isLocal: boolean; isBeta: boolean } {
  if (typeof window === 'undefined') {
    return { isLocal: false, isBeta: false }
  }
  const { href } = window.location
  return {
    isLocal: /localhost/.test(href),
    isBeta: /test/.test(href)
  }
}

/** 毫秒 → 分钟，保留 4 位小数，避免短停留全变成 0 */
export function msToMinutes(durationMs: number): number {
  return Math.round((durationMs / 60000) * 10000) / 10000
}

export class LxClass {
  private tracker: TrackerFn | null = null
  private cid = ''
  private valLab: ValLab = {}
  private environment: Record<string, string> = {}
  private pageViewCase: PageViewCase = null

  constructor() {
    if (typeof window === 'undefined' || typeof window.LXAnalytics !== 'function') {
      return
    }

    this.tracker = window.LXAnalytics('getTracker', 'mtlive_b') as TrackerFn

    const { isLocal, isBeta } = resolveEnv()
    if (isLocal) {
      this.environment.env = 'local'
    } else if (isBeta) {
      this.environment.env = 'beta'
    }
  }

  setParams(cid: string, valLab: ValLab = {}): void {
    this.cid = cid
    const currValLab = this.getValLab(valLab)
    this.valLab = currValLab
    this.tracker?.('setLxValLab', 'pageView', currValLab)
  }

  pageView(cid: string, valLab: ValLab = {}): void {
    try {
      // 同 cid 已有 pageCase 时不再重复 PV
      if (this.pageViewCase && this.cid === cid) return

      this.reset()
      this.setParams(cid, valLab)
      const pageCase = this.tracker?.(
        'pageView',
        { custom: this.getValLab(valLab) },
        {},
        { cid }
      ) as PageViewCase
      this.pageViewCase = typeof pageCase === 'function' ? pageCase : null
    } catch (error) {
      console.error('pageView err', error)
    }
  }

  moduleClick(bid: string, valLab: ValLab = {}, options: ValLab = {}): void {
    try {
      if (!this.pageViewCase) {
        if (!this.cid) return
        this.pageView(this.cid, this.getValLab({}))
      }
      this.pageViewCase?.('moduleClick', bid, this.getValLab(valLab), options)
    } catch (error) {
      console.error('moduleClick err', error)
    }
  }

  moduleView(bid: string, valLab: ValLab = {}, options: ValLab = {}): void {
    try {
      if (!this.pageViewCase) {
        if (!this.cid) return
        this.pageView(this.cid, this.getValLab({}))
      }
      this.pageViewCase?.('moduleView', bid, this.getValLab(valLab), options)
    } catch (error) {
      console.error('moduleView err', error)
    }
  }

  getValLab(valLab: ValLab = {}): ValLab {
    return { ...this.valLab, ...valLab }
  }

  reset(): void {
    this.pageViewCase = null
    this.cid = ''
    this.valLab = {}
    this.tracker?.('setLxValLab', 'pageView', {})
    this.environment = {}
  }
}

const lx = new LxClass()

export default lx

/**
 * 全站模块 MV 统一入口（start / end + 可选业务参数）
 *
 * @example
 * reportMv('b_xxx_mv', { module_type: 'start' })
 * reportMv('b_xxx_mv', { module_type: 'end', durationMs: 8200, module_identifier: '1' })
 */
export function reportMv(bid: string, params: ReportMvParams): void {
  const { module_type, durationMs, ...extra } = params
  const valLab: ValLab = { module_type, ...extra }

  if (module_type === 'end') {
    if (durationMs == null || durationMs <= 0) return
    valLab.duration = msToMinutes(durationMs)
  } else if(module_type === 'start') {
    valLab.duration = -99999
  }

  lx.moduleView(bid, valLab)
}
